export type RedactionRect = {
  id: string;
  /** Left edge as fraction of image width (0-1) */
  x: number;
  /** Top edge as fraction of image height (0-1) */
  y: number;
  /** Width as fraction of image width (0-1) */
  width: number;
  /** Height as fraction of image height (0-1) */
  height: number;
};

export const DEFAULT_RECT_WIDTH = 0.2;
export const DEFAULT_RECT_HEIGHT = 0.1;
export const MIN_RECT_SIZE = 0.05;
export const KEYBOARD_MOVE_STEP = 0.01;

export type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';

export function applyResizeDelta(
  startRect: RedactionRect,
  handle: ResizeHandle,
  dx: number,
  dy: number,
): RedactionRect {
  const anchorLeft = startRect.x;
  const anchorTop = startRect.y;
  const anchorRight = startRect.x + startRect.width;
  const anchorBottom = startRect.y + startRect.height;

  let x: number;
  let y: number;
  let width: number;
  let height: number;

  switch (handle) {
    case 'nw': {
      x = startRect.x + dx;
      y = startRect.y + dy;
      x = Math.max(0, Math.min(anchorRight - MIN_RECT_SIZE, x));
      y = Math.max(0, Math.min(anchorBottom - MIN_RECT_SIZE, y));
      width = anchorRight - x;
      height = anchorBottom - y;
      break;
    }
    case 'ne': {
      x = anchorLeft;
      y = startRect.y + dy;
      width = startRect.width + dx;
      y = Math.max(0, Math.min(anchorBottom - MIN_RECT_SIZE, y));
      width = Math.max(MIN_RECT_SIZE, Math.min(1 - anchorLeft, width));
      height = anchorBottom - y;
      break;
    }
    case 'sw': {
      x = startRect.x + dx;
      y = anchorTop;
      x = Math.max(0, Math.min(anchorRight - MIN_RECT_SIZE, x));
      width = anchorRight - x;
      height = startRect.height + dy;
      height = Math.max(MIN_RECT_SIZE, Math.min(1 - anchorTop, height));
      break;
    }
    case 'se': {
      x = anchorLeft;
      y = anchorTop;
      width = startRect.width + dx;
      height = startRect.height + dy;
      width = Math.max(MIN_RECT_SIZE, Math.min(1 - anchorLeft, width));
      height = Math.max(MIN_RECT_SIZE, Math.min(1 - anchorTop, height));
      break;
    }
  }

  return { ...startRect, x, y, width, height };
}

export function createRedactionRect(
  centerX: number,
  centerY: number,
  width = DEFAULT_RECT_WIDTH,
  height = DEFAULT_RECT_HEIGHT,
): RedactionRect {
  const clampedWidth = Math.max(width, MIN_RECT_SIZE);
  const clampedHeight = Math.max(height, MIN_RECT_SIZE);

  let x = centerX - clampedWidth / 2;
  let y = centerY - clampedHeight / 2;

  x = Math.max(0, Math.min(1 - clampedWidth, x));
  y = Math.max(0, Math.min(1 - clampedHeight, y));

  return {
    id: crypto.randomUUID(),
    x,
    y,
    width: clampedWidth,
    height: clampedHeight,
  };
}

export function clampRect(rect: RedactionRect): RedactionRect {
  const width = Math.max(MIN_RECT_SIZE, Math.min(1, rect.width));
  const height = Math.max(MIN_RECT_SIZE, Math.min(1, rect.height));
  const x = Math.max(0, Math.min(1 - width, rect.x));
  const y = Math.max(0, Math.min(1 - height, rect.y));

  return { ...rect, x, y, width, height };
}

export function rectFromCorners(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): Pick<RedactionRect, 'x' | 'y' | 'width' | 'height'> {
  const x = Math.min(x1, x2);
  const y = Math.min(y1, y2);

  return {
    x,
    y,
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}
